import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, Loader2, PlayCircle, FlaskConical } from "lucide-react";

// GL Integrity Audit — Sprint 1, Trial Balance Audit & Enhancement.
// Read-only audit over ledger_entries: branch coverage, voucher-type GL
// coverage (honest MISSING log), double-entry verification and Ledger
// Master health. Admin can run the idempotent branch backfill from here.

const pill = (status) => ({
  POSTING: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  MISSING: "bg-rose-100 text-rose-800 border border-rose-200",
  COVERED: "bg-sky-100 text-sky-800 border border-sky-200",
  LEGACY: "bg-amber-100 text-amber-800 border border-amber-200",
}[status] || "bg-slate-100 text-slate-600 border border-slate-200");

export default function GLAuditPage() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [exc, setExc] = useState({ items: [], total: 0 });
  const [excSkip, setExcSkip] = useState(0);
  const EXC_LIMIT = 25;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/ledger/gl-audit");
      setAudit(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load GL audit");
    } finally {
      setLoading(false);
    }
  };

  const loadExceptions = async (skip = 0) => {
    try {
      const r = await api.get(`/ledger/gl-audit/branch-exceptions?skip=${skip}&limit=${EXC_LIMIT}`);
      setExc(r.data);
      setExcSkip(skip);
    } catch {
      /* permission or empty — leave table blank */
    }
  };

  useEffect(() => { load(); loadExceptions(0); }, []);

  const runBackfill = async (dryRun) => {
    if (!dryRun && !window.confirm(
      "Run the GL Branch Backfill now?\n\nThis stamps branch_id onto every ledger entry that lacks one (source voucher → invoice → project chain). Unresolvable rows are left blank and listed in the exception report. The operation is idempotent."
    )) return;
    setRunning(true);
    try {
      const r = await api.post(`/ledger/gl-audit/run-branch-backfill?dry_run=${dryRun}`);
      const s = r.data;
      toast.success(
        `${dryRun ? "DRY RUN — " : ""}Scanned ${s.total_scanned}, stamped ${s.stamped}, exceptions ${s.exceptions}`
      );
      if (!dryRun) { await load(); await loadExceptions(0); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setRunning(false);
    }
  };

  if (!audit) {
    return (
      <div className="text-slate-500 p-6" data-testid="gl-audit-loading">
        {loading ? "Loading GL audit…" : "GL audit unavailable"}
      </div>
    );
  }

  const bc = audit.branch_coverage || {};
  const de = audit.double_entry || {};
  const lm = audit.ledger_master || {};
  const vouchers = audit.voucher_coverage || [];
  const missing = vouchers.filter(v => v.status === "MISSING");

  return (
    <div data-testid="gl-audit-page">
      <PageHeader
        eyebrow="Accounting — Sprint 1"
        title="GL Integrity Audit"
        subtitle="Branch coverage, voucher-type GL coverage, double-entry verification and Ledger Master health — an honest snapshot of the General Ledger."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { load(); loadExceptions(0); }} disabled={loading} data-testid="gl-audit-refresh">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={() => runBackfill(true)} disabled={running} data-testid="gl-audit-dryrun-btn">
              <FlaskConical className="w-4 h-4 mr-1" /> Dry Run Backfill
            </Button>
            <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={() => runBackfill(false)} disabled={running} data-testid="gl-audit-run-backfill-btn">
              {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1" />}
              Run Branch Backfill
            </Button>
          </div>
        }
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">GL Entries</div>
          <div className="font-display text-2xl font-black mt-2 text-slate-900" data-testid="gl-audit-total-entries">{bc.total_entries ?? 0}</div>
        </Card>
        <Card className={`p-5 ${(bc.coverage_pct ?? 0) >= 99.99 ? "border-emerald-300" : "border-amber-300"}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Branch Coverage</div>
          <div className="font-display text-2xl font-black mt-2 text-slate-900" data-testid="gl-audit-branch-coverage">{bc.coverage_pct ?? 0}%</div>
          <div className="text-xs text-slate-500 mt-1">{bc.without_branch ?? 0} unmapped · {bc.exception_count ?? 0} exceptions</div>
        </Card>
        <Card className={`p-5 ${de.balanced ? "border-emerald-300" : "border-rose-300"}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1"><ShieldCheck size={11} /> Double Entry</div>
          <div className={`font-display text-2xl font-black mt-2 ${de.balanced ? "text-emerald-700" : "text-rose-700"}`} data-testid="gl-audit-double-entry">
            {de.balanced ? "BALANCED" : `Δ ${formatINR(de.difference || 0)}`}
          </div>
          <div className="text-xs text-slate-500 mt-1">{de.unbalanced_voucher_count ?? 0} unbalanced voucher(s)</div>
        </Card>
        <Card className={`p-5 ${missing.length === 0 ? "border-emerald-300" : "border-amber-300"}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">GL-Missing Voucher Types</div>
          <div className="font-display text-2xl font-black mt-2 text-slate-900" data-testid="gl-audit-missing-types">{missing.length}</div>
          <div className="text-xs text-slate-500 mt-1">see notes — pending client decision</div>
        </Card>
      </div>

      {/* Voucher-type GL coverage */}
      <Card className="overflow-hidden mb-6">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="text-sm font-bold text-slate-800">Voucher-Type GL Coverage</div>
          <div className="text-xs text-slate-500">Which modules post double-entries to the General Ledger — and which are honestly logged as MISSING.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="gl-audit-voucher-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>{["Module", "Status", "Vouchers", "With GL", "Without GL", "Orphan GL Refs", "Note"].map(h =>
                <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2.5 px-4">{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {vouchers.map(v => (
                <tr key={v.ref_type} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`gl-audit-voucher-${v.ref_type}`}>
                  <td className="py-2 px-4 font-medium text-slate-800">{v.module}</td>
                  <td className="py-2 px-4"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${pill(v.status)}`}>{v.status}</span></td>
                  <td className="py-2 px-4 tabular">{v.total_vouchers}</td>
                  <td className="py-2 px-4 tabular">{v.vouchers_with_gl}</td>
                  <td className="py-2 px-4 tabular">{v.vouchers_without_gl}</td>
                  <td className={`py-2 px-4 tabular ${v.orphan_gl_refs ? "text-amber-700 font-semibold" : ""}`}>{v.orphan_gl_refs}</td>
                  <td className="py-2 px-4 text-xs text-slate-500 max-w-[320px]">{v.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Branch coverage by ref_type */}
        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <div className="text-sm font-bold text-slate-800">Branch Coverage by Ref Type</div>
          </div>
          <table className="w-full text-sm" data-testid="gl-audit-branch-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>{["Ref Type", "Total", "With Branch", "Unmapped"].map(h =>
                <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2.5 px-4">{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {(bc.by_ref_type || []).map(r => (
                <tr key={r.ref_type} className="border-b border-slate-100">
                  <td className="py-2 px-4 text-xs">{r.ref_type}</td>
                  <td className="py-2 px-4 tabular">{r.total}</td>
                  <td className="py-2 px-4 tabular text-emerald-700">{r.with_branch}</td>
                  <td className={`py-2 px-4 tabular ${r.without_branch ? "text-rose-700 font-semibold" : "text-slate-400"}`}>{r.without_branch}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {/* Ledger master health + top unbalanced */}
        <div className="space-y-6">
          <Card className="p-4">
            <div className="text-sm font-bold text-slate-800 mb-2">Ledger Master Health</div>
            <div className="grid grid-cols-2 gap-2 text-xs" data-testid="gl-audit-master-health">
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Total ledgers</span><span className="font-semibold">{lm.total}</span></div>
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">System</span><span className="font-semibold">{lm.system}</span></div>
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">User</span><span className="font-semibold">{lm.user}</span></div>
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Missing group</span><span className={`font-semibold ${lm.missing_group ? "text-rose-700" : ""}`}>{lm.missing_group}</span></div>
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Missing Schedule III</span><span className={`font-semibold ${lm.missing_schedule_iii ? "text-rose-700" : ""}`}>{lm.missing_schedule_iii}</span></div>
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Duplicate codes</span><span className={`font-semibold ${(lm.duplicate_codes || []).length ? "text-rose-700" : ""}`}>{(lm.duplicate_codes || []).length}</span></div>
              <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Duplicate names</span><span className={`font-semibold ${(lm.duplicate_names || []).length ? "text-rose-700" : ""}`}>{(lm.duplicate_names || []).length}</span></div>
            </div>
          </Card>

          {audit.fixed_assets && (
            <Card className="p-4" data-testid="gl-audit-fixed-assets">
              <div className="text-sm font-bold text-slate-800 mb-1">Fixed Assets — FAR ↔ GL Control</div>
              <div className="text-xs text-slate-500 mb-2">{audit.fixed_assets.note}</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">GL control balance</span><span className="font-semibold tabular">{formatINR(audit.fixed_assets.gl_control_balance)}</span></div>
                <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">FAR (PI-linked, {audit.fixed_assets.far_linked_count})</span><span className="font-semibold tabular">{formatINR(audit.fixed_assets.far_linked_value)}</span></div>
                <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Difference</span><span className={`font-semibold tabular ${audit.fixed_assets.reconciled ? "text-emerald-700" : "text-amber-700"}`}>{formatINR(audit.fixed_assets.difference)}</span></div>
                <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">Legacy (no PI link, {audit.fixed_assets.far_legacy_count})</span><span className="font-semibold tabular text-slate-400">{formatINR(audit.fixed_assets.far_legacy_value)}</span></div>
              </div>
              <div className="mt-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${audit.fixed_assets.reconciled ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "bg-amber-100 text-amber-800 border border-amber-200"}`} data-testid="gl-audit-far-status">
                  {audit.fixed_assets.reconciled ? "RECONCILED" : "PENDING RECONCILIATION"}
                </span>
              </div>
            </Card>
          )}

          <Card className="overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
              <div className="text-sm font-bold text-slate-800">Top Unbalanced Vouchers (Dr ≠ Cr)</div>
              <div className="text-xs text-slate-500">Recorded for the Data Integrity Remediation sprint — NOT auto-fixed.</div>
            </div>
            <table className="w-full text-sm" data-testid="gl-audit-unbalanced-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>{["Ref", "Type", "Debit", "Credit", "Diff"].map(h =>
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {(de.top_unbalanced || []).map((u, i) => (
                  <tr key={`${u.ref_id}-${i}`} className="border-b border-slate-100">
                    <td className="py-1.5 px-3 text-xs font-mono">{u.ref_number || u.ref_id?.slice(0, 8)}</td>
                    <td className="py-1.5 px-3 text-xs">{u.ref_type}</td>
                    <td className="py-1.5 px-3 tabular text-xs">{formatINR(u.debit)}</td>
                    <td className="py-1.5 px-3 tabular text-xs">{formatINR(u.credit)}</td>
                    <td className="py-1.5 px-3 tabular text-xs font-semibold text-rose-700">{formatINR(u.diff)}</td>
                  </tr>
                ))}
                {(de.top_unbalanced || []).length === 0 && (
                  <tr><td colSpan={5} className="py-3 px-3 text-xs text-emerald-700">All vouchers balance — Dr equals Cr on every posting group.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </div>
      </div>

      {/* Branch backfill exceptions */}
      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-slate-800">Branch Backfill Exceptions</div>
            <div className="text-xs text-slate-500">Entries left WITHOUT a branch (source deleted and no project mapping). Kept blank by design — no guessing.</div>
          </div>
          <div className="text-xs text-slate-500">{exc.total} total</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="gl-audit-exceptions-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>{["Date", "Ref Type", "Ref Number", "Account", "Debit", "Credit", "Reason"].map(h =>
                <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2.5 px-4">{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {(exc.items || []).map(x => (
                <tr key={x.id} className="border-b border-slate-100">
                  <td className="py-2 px-4 text-xs">{(x.entry_date || "").slice(0, 10)}</td>
                  <td className="py-2 px-4 text-xs">{x.ref_type}</td>
                  <td className="py-2 px-4 text-xs font-mono">{x.ref_number || x.ref_id?.slice(0, 8)}</td>
                  <td className="py-2 px-4 text-xs">{x.account_name}</td>
                  <td className="py-2 px-4 tabular text-xs">{x.debit ? formatINR(x.debit) : ""}</td>
                  <td className="py-2 px-4 tabular text-xs">{x.credit ? formatINR(x.credit) : ""}</td>
                  <td className="py-2 px-4 text-xs text-amber-700">{x.reason}</td>
                </tr>
              ))}
              {(exc.items || []).length === 0 && (
                <tr><td colSpan={7} className="py-4 px-4 text-xs text-slate-500" data-testid="gl-audit-exceptions-empty">No exceptions recorded. Run the backfill to (re)generate the exception report.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {exc.total > EXC_LIMIT && (
          <div className="flex items-center justify-end gap-2 p-3 text-xs">
            <button className="border rounded px-2 py-1 disabled:opacity-40" disabled={excSkip <= 0}
              onClick={() => loadExceptions(Math.max(0, excSkip - EXC_LIMIT))} data-testid="gl-audit-exc-prev">‹ Prev</button>
            <span className="text-slate-500">{excSkip + 1}–{Math.min(excSkip + EXC_LIMIT, exc.total)} of {exc.total}</span>
            <button className="border rounded px-2 py-1 disabled:opacity-40" disabled={excSkip + EXC_LIMIT >= exc.total}
              onClick={() => loadExceptions(excSkip + EXC_LIMIT)} data-testid="gl-audit-exc-next">Next ›</button>
          </div>
        )}
      </Card>
    </div>
  );
}
